import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { getFormValues } from 'redux-form';

import type TimeCardEntryVM from 'ab-viewModels/timeSheet/timeCardEntry.viewModel';
import type TimeCardRejectedTimeSheetVM from 'ab-viewModels/timeSheet/timeCardRejectedTimeSheet.viewModel';
import type { TimeSheetVM } from 'ab-viewModels/timeSheet/timeSheet.viewModel';
import type { TimelineEntitesForAccount } from 'ab-viewModels/timeSheet/timeSheetEntry.viewModel';
import type { TimeSheetTrackedEntryVM } from 'ab-viewModels/timeSheet/trackedEntries.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import type { OverlapMeta } from '@acceligentllc/shared/utils/timeSheetEntry';
import * as TimeSheetEntryUtils from '@acceligentllc/shared/utils/timeSheetEntry';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimeSheetApprovalStatus } from '@acceligentllc/shared/enums/timeSheetApprovalStatus';
import { TimeSheetSignatureStatus } from '@acceligentllc/shared/enums/timeSheetSignatureStatus';
import { WorkOrderReviewStatus } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import type { RootState } from 'af-reducers';

import type TimeZoneSourceType from 'ab-enums/timezoneSourceType.enum';

import * as FORMS from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';

import * as TimeSheetActions from 'af-actions/timeSheet';

import styles from './styles.module.scss';
import Timeline from './Timeline/index';
import TimeSheetApprovalActions from './TimeSheetApprovalActions/TimeSheetApprovalActions';
import TimeSheetDurations from './TimeSheetDurations/TimeSheetDurations';
import TimeSheetInfo from './TimeSheetInfo/TimeSheetInfo';
import TimeSheetSignature from './TimeSheetInfo/TimeSheetSignature';
import TimeSheetNote from './TimeSheetNote';
import TimeSheetRowActions from './TimeSheetRowActions';
import TimeSheetTime from './TimeSheetTime';
import TimeSheetTimeSplits from './TimeSheetTimeSplits/TimeSheetTimeSplits';
import RejectModal from '../../../Shared/RejectTimeSheetModal';
import type SignatureForm from '../../../Shared/SignatureModal/FormModel';
import AssignUnassignedEntriesModal from '../../Modals/AssignUnassignedEntriesModal';
import type { CertificationStatusForm } from '../../Modals/CertificationStatusModal';
import CompanyPolicyModal from '../../Modals/CertificationStatusModal/CompanyPolicyModal';
import CertificationStatusModal from '../../Modals/CertificationStatusModal/index';
import SignTimeSheetModal from '../../Modals/SignTimeSheetModal';
import TimeCardTroubleshootModal from '../../Modals/TimeCardTroubleshootModal';
import TimelineHistoryModal from '../../Modals/TimelineHistoryModal';
import TimelineSubmitSuccessModal from '../../Modals/TimelineSubmitSuccessModal';
import TimeSheetNoteModal from '../../Modals/TimeSheetNoteModal';

interface TimeSheetWithPerDiem extends TimeSheetVM {
	perDiemInfo: {
		perDiemFlag: boolean;
		perDiemAmount: Nullable<string>;
	};
}

interface OwnProps {
	/** False if the user isn't on the latest published version of WO */
	active: boolean;
	allAccountEntries: TimeCardEntryVM[];
	code: string;
	hasEntryChanges: boolean;
	isManagerOrAdmin: boolean;
	isAccountingOrAdmin: boolean;
	isSuperintendent: boolean;
	isTemporaryEmployee: boolean;
	canManagePerDiem: boolean;
	onEdit: (timeSheet: TimeSheetVM) => void;
	onTimeSheetSubmit: (form: SignatureForm) => Promise<void>;
	isTimeSheetReadOnly: boolean;
	timeSheet: TimeSheetWithPerDiem;
	workOrderId: number;
	isOwner: boolean;
	areReportsReadOnly: boolean;
	trackedEntry: Nullable<TimeSheetTrackedEntryVM>;
	timeSheetEntities: TimelineEntitesForAccount[];
	timeSheetOverlaps: Nullable<Record<string, OverlapMeta>>;
	timeZoneInUse: Nullable<string>;
	timezoneSourceType: Nullable<TimeZoneSourceType>;
	isDuplicateName: boolean;
	rejectedTimeSheetsForTimeCards: TimeCardRejectedTimeSheetVM[];
	onUpdateTimeSheetEntry: () => void;
	onEndTimeShift: (timeSheetId: Nullable<number>) => void;
	workOrderReviewStatus: WorkOrderReviewStatus;
	canReject: boolean;
	shouldRoundTo1Min: boolean;
	continueToSignatureModalForTimeSheet: Nullable<number>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

interface State {
	employee: { name: string; status: Nullable<TimeSheetSignatureStatus>; accountId: number; }[];
	hasOverlaps: boolean;
	showEquipmentActionButton: boolean;
	showEquipmentModal: boolean;
	showEquipmentTimeError: boolean;
	showNoChangesOnRejectModal: boolean;
	showNoteModal: boolean;
	showTimeCardTroubleshootModal: boolean;
	showAssignUnassignedModal: boolean;
	showRejectModal: boolean;
	showSignButton: boolean;
	showSignatureModal: boolean;
	showTimelineHistoryModal: boolean;
	showTimelineSubmitSuccessModal: boolean;
	shouldShowRemoveTimeSheetModal: boolean;
	shouldShowInvalidTimesheetModal: boolean;
	showCertificationStatusModal: boolean;
	showCompanyPolicyModal: boolean;
	showEndShiftConfirmationModal: boolean;
	certificationStatusData: Nullable<CertificationStatusForm>;
}

class TimeSheetRow extends React.PureComponent<Props, State> {

	state: State = {
		employee: [TimeSheetRow.getEmployee(this.props.timeSheet)],
		hasOverlaps: false,
		showRejectModal: false,
		showSignatureModal: false,
		showTimelineSubmitSuccessModal: false,
		showSignButton: false,
		showEquipmentModal: false,
		showNoteModal: false,
		showTimeCardTroubleshootModal: false,
		showAssignUnassignedModal: false,
		showEquipmentTimeError: false,
		showEquipmentActionButton: false,
		showNoChangesOnRejectModal: false,
		showTimelineHistoryModal: false,
		shouldShowRemoveTimeSheetModal: false,
		shouldShowInvalidTimesheetModal: false,
		showCertificationStatusModal: false,
		showCompanyPolicyModal: false,
		showEndShiftConfirmationModal: false,
		certificationStatusData: null,
	};

	static INVALID_TIMESHEET = (
		<>
			You cannot sign this Time Sheet.
			<br />
			Time sheet entries overlap with each other.
			<br />
			Please resolve error and proceed with Sign.
		</>
	);

	static END_SHIFT_MODAL_BODY = (
		<>
			You are about to sign your Time Sheet.
			<br />
			Your Time Card is still recording your time and this action will end your shift.
			<br />
			Do you want to end your shift and sign your Time Sheet?
		</>
	);

	static getDerivedStateFromProps(props: Props) {
		const {
			timeSheet: {
				employeeApprovalStatus,
				superintendentApprovalStatus,
				timeSplitEntries,
				totalJobDuration,
			},
			isOwner,
			areReportsReadOnly,
			isTemporaryEmployee,
		} = props;

		const isSigned = employeeApprovalStatus.signatureStatus === TimeSheetSignatureStatus.SIGNED;

		const showSignButton = isOwner
			&& !isSigned
			&& !isTemporaryEmployee
			&& superintendentApprovalStatus.approvalStatus !== TimeSheetApprovalStatus.APPROVED;

		const showEquipmentActionButton = !areReportsReadOnly;

		let totalEquipmentTime = 0;
		if (timeSplitEntries?.length > 0) {
			totalEquipmentTime = timeSplitEntries.map((ts) => ts.time).reduce((currEqTime, aggregateTime) => currEqTime + aggregateTime);
		}
		const showEquipmentTimeError = totalEquipmentTime > totalJobDuration;

		return {
			showSignButton,
			showEquipmentTimeError,
			showEquipmentActionButton,
		};
	}

	static getEmployee = (sheet: TimeSheetVM) => {
		return {
			name: sheet.userFullName,
			accountId: sheet.accountId,
			status: sheet.employeeApprovalStatus.signatureStatus,
		};
	};

	static wasSignedAfterRejection = (timeSheet: TimeSheetVM) => {
		return timeSheet.wasRejected
			&& timeSheet.employeeApprovalStatus?.signatureStatus === TimeSheetSignatureStatus.SIGNED
			&& timeSheet.superintendentApprovalStatus?.approvalStatus !== TimeSheetApprovalStatus.APPROVED;
	};

	static wasNotModified = (timeSheet: TimeSheetVM) => {
		return timeSheet.superintendentApprovalStatus?.approvalStatus === TimeSheetApprovalStatus.REJECTED && !timeSheet.wasModifiedAfterRejection;
	};

	static NO_CHANGES_ON_REJECTED_TIME_SHEET = (workOrderCode: string, rejectedBy: Nullable<string>) => (
		<>
			Your Time Sheet for <strong>{workOrderCode}</strong> has been rejected{!!rejectedBy && <> by <strong>{rejectedBy}</strong></>}.
			<br />
			<br />
			If you proceed  you will sign unchanged Time Sheet. You can leave a note to Superintendent when signing.
			<br />
			<br />
			Do you want to proceed to signature?
		</>
	);

	static REMOVE_FIELD_WORKER_MODAL_BODY = (fieldWorker: string, workOrder: string) => (
		<>
			You are about to Remove {fieldWorker}'s Timesheet from Work Order {workOrder}.
		</>
	);

	componentDidMount() {
		const { timeSheet, continueToSignatureModalForTimeSheet, shouldRoundTo1Min, onEndTimeShift } = this.props;
		if (timeSheet.id === continueToSignatureModalForTimeSheet) {
			if (shouldRoundTo1Min) {
				this.showCertificationStatusModal();
			} else {
				this.showSignatureModal();
			}
		}
		onEndTimeShift(null);
		this.findOverlaps();
	}

	componentDidUpdate(prevProps: Props) {
		const { timeSheet } = this.props;
		if (prevProps.timeSheet !== timeSheet) {
			this.findOverlaps();
		}
	}

	findOverlaps = async () => {
		const { timeSheet, hasTimeSheetOverlaps, isOwner } = this.props;

		if (isOwner) {
			const { hasOverlaps } = await hasTimeSheetOverlaps(timeSheet.id);
			this.setState(() => ({ hasOverlaps }));
		}
	};

	static offsetTimelineEntry(entry: TimeCardEntryVM, offset: number): TimeCardEntryVM {
		return {
			...entry,
			startTime: TimeUtils.offsetTime(entry.startTime, offset, 'minutes', TimeFormat.ISO_DATETIME) ?? entry.startTime,
			endTime: entry.endTime ? TimeUtils.offsetTime(entry.endTime, offset, 'minutes', TimeFormat.ISO_DATETIME) : null,
		};
	}

	showRejectModal = () => this.setState(({ showRejectModal: true }));
	hideRejectModal = () => this.setState(({ showRejectModal: false }));

	showSignatureModal = () => this.setState(() => ({ showSignatureModal: true }));
	hideSignatureModal = () => this.setState(() => ({ showSignatureModal: false }));

	showTimelineSubmitSuccessModal = () => this.setState(() => ({ showTimelineSubmitSuccessModal: true }));
	hideTimelineSubmitSuccessModal = () => this.setState(() => ({ showTimelineSubmitSuccessModal: false }));

	showEquipmentModal = () => this.setState(() => ({ showEquipmentModal: true }));
	hideEquipmentModal = () => this.setState(() => ({ showEquipmentModal: false }));

	showNoteModal = () => this.setState(() => ({ showNoteModal: true }));
	hideNoteModal = () => this.setState(() => ({ showNoteModal: false }));

	showTimeCardTroubleshootModal = () => this.setState(() => ({ showTimeCardTroubleshootModal: true }));
	hideTimeCardTroubleshootModal = () => this.setState(() => ({ showTimeCardTroubleshootModal: false }));

	showAssignUnassignedModal = () => this.setState(() => ({ showAssignUnassignedModal: true, showTimeCardTroubleshootModal: false }));
	hideAssignUnassignedModal = () => this.setState(() => ({ showAssignUnassignedModal: false }));

	showNoChangesOnRejectModal = () => this.setState(() => ({ showNoChangesOnRejectModal: true }));
	hideNoChangesOnRejectModal = () => this.setState(() => ({ showNoChangesOnRejectModal: false }));

	showTimelineHistoryModal = () => this.setState(() => ({ showTimelineHistoryModal: true }));
	hideTimelineHistoryModal = () => this.setState(() => ({ showTimelineHistoryModal: false }));

	showRemoveTimeSheetModal = () => this.setState(() => ({ shouldShowRemoveTimeSheetModal: true }));
	hideRemoveTimeSheetModal = () => this.setState(() => ({ shouldShowRemoveTimeSheetModal: false }));

	showInvalidTimesheetModal = () => this.setState(() => ({ shouldShowInvalidTimesheetModal: true }));
	hideInvalidTimesheetModal = () => this.setState(() => ({ shouldShowInvalidTimesheetModal: false }));

	showCertificationStatusModal = () => this.setState(() => ({ showCertificationStatusModal: true }));
	hideCertificationStatusModal = () => this.setState(() => ({ showCertificationStatusModal: false }));

	showEndShiftConfirmationModal = () => this.setState(() => ({ showEndShiftConfirmationModal: true }));
	hideEndShiftConfirmationModal = () => this.setState(() => ({ showEndShiftConfirmationModal: false }));

	handleSignTimeSheet = async () => {
		const { timeSheet, hasTimeSheetOverlaps, shouldRoundTo1Min, trackedEntry } = this.props;

		if (TimeSheetRow.wasNotModified(timeSheet)) {
			this.showNoChangesOnRejectModal();
		} else {
			const { hasOverlaps } = await hasTimeSheetOverlaps(timeSheet.id);
			this.setState(() => ({ hasOverlaps }));
			if (hasOverlaps) {
				// should never trigger, here in case of socket event error
				this.showInvalidTimesheetModal();
			} else if (trackedEntry) {
				this.showEndShiftConfirmationModal();
			} else if (shouldRoundTo1Min) {
				this.showCertificationStatusModal();
			} else {
				this.showSignatureModal();
			}
		}
	};

	handleEdit = () => {
		const { onEdit, timeSheet } = this.props;

		onEdit(timeSheet);
	};

	onTimeSheetSubmit = async (form: SignatureForm) => {
		const { onTimeSheetSubmit, shouldRoundTo1Min } = this.props;
		const { certificationStatusData } = this.state;

		if (shouldRoundTo1Min) {
			form.certificationData = certificationStatusData;
		}

		await onTimeSheetSubmit(form);
		this.setState(() => ({
			showSignatureModal: false,
			showTimelineSubmitSuccessModal: true,
		}));
	};

	onCertStatusSubmit = async (certData: CertificationStatusForm) => {
		this.setState(() => ({
			showCertificationStatusModal: false,
			showSignatureModal: true,
			certificationStatusData: certData,
		}));
	};

	onCompanyPolicyOpen = async () => {
		this.setState(() => ({
			showCertificationStatusModal: false,
			showCompanyPolicyModal: true,
		}));
	};

	onCompanyPolicyClose = async () => {
		this.setState(() => ({
			showCompanyPolicyModal: false,
			showCertificationStatusModal: true,
		}));
	};

	onContinueToSignatureModal = async () => {
		const { shouldRoundTo1Min } = this.props;

		if (shouldRoundTo1Min) {
			this.showCertificationStatusModal();
		} else {
			this.showSignatureModal();
		}
	};

	confirmEndShift = async () => {
		const { endShift, timeSheet: { id: timeSheetId, accountId }, trackedEntry, onEndTimeShift } = this.props;

		if (trackedEntry) {
			await endShift(accountId, {
				endTime: TimeUtils.formatDate(new Date(), TimeFormat.ISO_DATETIME),
				entryIds: [trackedEntry.id],
			});
		}

		onEndTimeShift(timeSheetId);
		this.hideEndShiftConfirmationModal();
	};

	handleRemove = async () => {
		this.showRemoveTimeSheetModal();
	};

	onRemove = async () => {
		const { removeTimeSheet, timeSheet: { id: timeSheetId } } = this.props;
		await removeTimeSheet(timeSheetId);
	};

	render() {
		const {
			timeSheet: {
				id,
				accountId,
				userFullName,
				dueDate,
				employeeApprovalStatus,
				superintendentApprovalStatus,
				totalJobDuration,
				timeSplitEntries,
				endTime,
				note,
				startTime,
				totalBreakDuration,
				totalTravelDuration,
				totalShopDuration,
				temporaryEmployeeUniqueId: userUniqueId,
				perDiemInfo,
				workShifts,
			},
			areReportsReadOnly,
			isTimeSheetReadOnly,
			code,
			workOrderId,
			isOwner,
			isManagerOrAdmin,
			isAccountingOrAdmin,
			isSuperintendent,
			isTemporaryEmployee,
			active,
			trackedEntry,
			timeSheetEntities,
			timeSheetOverlaps,
			timeZoneInUse,
			timezoneSourceType,
			hasEntryChanges,
			isDuplicateName,
			allAccountEntries,
			rejectedTimeSheetsForTimeCards,
			onUpdateTimeSheetEntry,
			canManagePerDiem,
			workOrderReviewStatus,
			canReject,
		} = this.props;

		const {
			hasOverlaps,
			showRejectModal,
			showSignatureModal,
			showTimelineSubmitSuccessModal,
			showNoteModal,
			showTimeCardTroubleshootModal,
			showAssignUnassignedModal,
			showSignButton,
			showEquipmentTimeError,
			showEquipmentActionButton,
			showNoChangesOnRejectModal,
			showTimelineHistoryModal,
			shouldShowRemoveTimeSheetModal,
			shouldShowInvalidTimesheetModal,
			showCertificationStatusModal,
			showCompanyPolicyModal,
			showEndShiftConfirmationModal,
		} = this.state;

		const isApproved = superintendentApprovalStatus.approvalStatus === TimeSheetApprovalStatus.APPROVED;
		const isReadOnly = areReportsReadOnly && isTimeSheetReadOnly;
		const isModified = TimeSheetRow.wasSignedAfterRejection(this.props.timeSheet);

		const timeSectionStyle = `${styles['time-sheet-list__row__info__total-time-section']} ${styles['time-sheet-list__row__info__info-section']}`;
		const modifiedStyle = isModified ? ` ${styles['time-sheet-list__row__info--modified']}` : '';
		const modalContainerStyle = `${styles['time-sheet-list__row__info']}${modifiedStyle} row`;

		const totalTime = totalJobDuration + totalShopDuration + totalTravelDuration;

		const showOverlaps = isSuperintendent || isManagerOrAdmin;
		const browserOffset = (new Date()).getTimezoneOffset();
		const timeZoneOffset = timeZoneInUse ? TimeUtils.getOffset(timeZoneInUse) : 0;

		const timeOffset = timeZoneOffset + browserOffset;
		const entriesWithOffset = allAccountEntries?.map((_entry) => TimeSheetRow.offsetTimelineEntry(_entry, timeOffset)) ?? [];

		// If time entries are recorded/created in another time zone, we want to check work time to decide if unassigned entries are available for WO
		const entriesBasedOnTimeZone = timeZoneInUse ? entriesWithOffset : allAccountEntries;

		const numberOfTotalUnassignedEntries = allAccountEntries?.filter((_entry) => _entry.workOrderId === null && _entry.endTime).length ?? 0;

		const availableUnassignedEntries: TimeCardEntryVM[] = entriesBasedOnTimeZone?.filter(
			(_tse) => {
				const timeSheetStartTimeDate = TimeUtils.getDateFromIsoDateTime(_tse.startTime);
				const isEntryForToday = dueDate === timeSheetStartTimeDate;
				const isEntryForTomorrow = TimeUtils.formatDate(TimeUtils.addDays(dueDate, 1, TimeFormat.DB_DATE_ONLY),
					TimeFormat.DB_DATE_ONLY) === timeSheetStartTimeDate;

				return _tse.workOrderId === null
					&& _tse.endTime
					&& (isEntryForToday || isEntryForTomorrow);
			}
		);
		const accountOverlaps = allAccountEntries ? TimeSheetEntryUtils.findOverlaps(allAccountEntries) ?? {} : null;

		const rejectedTSForAccount = rejectedTimeSheetsForTimeCards.filter((_ts) => accountId === _ts.accountId);

		const eligableForRemoval = !active && !timeSheetEntities.length && !startTime && workOrderReviewStatus !== WorkOrderReviewStatus.FINAL;

		return (
			<div className={styles['time-sheet-list__row']}>
				<div className={styles['time-sheet-list__row__name-and-action-section']}>
					<TimeSheetInfo
						active={active}
						canManagePerDiem={canManagePerDiem}
						isApproved={isApproved}
						isDuplicateName={isDuplicateName}
						isOwner={isOwner}
						isTemporaryEmployee={isTemporaryEmployee}
						overlaps={timeSheetOverlaps}
						perDiemInfoForEmployee={perDiemInfo}
						showOverlaps={showOverlaps}
						timeSheetEntities={timeSheetEntities}
						userFullName={userFullName}
						userUniqueId={userUniqueId}
					/>
					<TimeSheetRowActions
						eligableForRemoval={eligableForRemoval}
						handleEdit={this.handleEdit}
						isAccountingOrAdmin={isAccountingOrAdmin}
						isManagerOrAdmin={isManagerOrAdmin}
						isOwner={isOwner}
						isReadOnly={isReadOnly}
						isSuperIntendent={isSuperintendent}
						openViewHistoryModal={this.showTimelineHistoryModal}
						remove={this.handleRemove}
					/>

				</div>
				<div className={styles['time-sheet-list__row__info']}>
					<div className={styles['time-sheet-list__row__info__info-section']}>
						<TimeSheetSignature
							approvalStatus={superintendentApprovalStatus}
							isOwner={isOwner}
							isTemporaryEmployee={isTemporaryEmployee}
							signatureStatus={employeeApprovalStatus}
							timeOffset={timeZoneOffset}
						/>
						<TimeSheetApprovalActions
							hasOverlaps={hasOverlaps}
							onRejectButtonClick={this.showRejectModal}
							onSignButtonClick={this.handleSignTimeSheet}
							showRejectButton={canReject}
							showSignButton={showSignButton}
						/>
					</div>
					<div className={timeSectionStyle}>
						{(isManagerOrAdmin || isSuperintendent) && !isTemporaryEmployee && <div className={styles['time-sheet-list__row__info__time-card-link']}>
							{numberOfTotalUnassignedEntries ?
								<Button
									icon="ActionSearch"
									label="Unassigned time entries"
									onClick={this.showTimeCardTroubleshootModal}
									style="link-danger"
								/>
								:
								<Button
									icon="ActionSearch"
									label="Open Time Card Troubleshooting View"
									onClick={this.showTimeCardTroubleshootModal}
									style="link"
								/>
							}
						</div>}

						<TimeSheetTime
							endTime={endTime}
							startTime={startTime}
							timeZoneOffset={timeZoneOffset}
							totalTime={totalTime}
							workShifts={workShifts}
						/>
						<TimeSheetNote
							note={note}
							openNoteModal={this.showNoteModal}
							showNoteActionButton={!isTimeSheetReadOnly}
						/>
					</div>
					<div className={styles['time-sheet-list__row__info__info-section']}>
						<TimeSheetDurations
							breakDuration={totalBreakDuration}
							jobDuration={totalJobDuration}
							shopDuration={totalShopDuration}
							showEquipmentTimeError={showEquipmentTimeError}
							timeSplitEntries={timeSplitEntries}
							trackedEntry={trackedEntry}
							travelDuration={totalTravelDuration}
						/>
						<TimeSheetTimeSplits
							isManagerOrAdmin={isManagerOrAdmin}
							isSuperintendent={isSuperintendent}
							showEquipmentActionButton={showEquipmentActionButton}
							showEquipmentModal={this.showEquipmentModal}
							showEquipmentTimeError={showEquipmentTimeError}
							timeSplitEntries={timeSplitEntries}
							totalJobDuration={totalJobDuration}
						/>
					</div>
				</div>
				<Timeline
					dueDate={this.props.timeSheet.dueDate}
					employeeApprovalStatus={employeeApprovalStatus}
					entities={timeSheetEntities}
					isManagerOrAdmin={isManagerOrAdmin}
					isSuperIntendent={isSuperintendent}
					overlaps={timeSheetOverlaps}
					showTimeline={hasEntryChanges}
					superintendentApprovalStatus={superintendentApprovalStatus}
				/>
				<div className={modalContainerStyle}>
					<RejectModal
						accountId={accountId}
						close={this.hideRejectModal}
						code={code}
						dueDate={dueDate}
						employeeFullName={userFullName}
						showModal={showRejectModal}
						workOrderId={workOrderId}
					/>
					<SignTimeSheetModal
						closeModal={this.hideSignatureModal}
						showModal={showSignatureModal}
						submit={this.onTimeSheetSubmit}
					/>
					<TimelineSubmitSuccessModal
						closeModal={this.hideTimelineSubmitSuccessModal}
						showModal={showTimelineSubmitSuccessModal}
					/>
					<TimeSheetNoteModal
						accountId={accountId}
						closeModal={this.hideNoteModal}
						showModal={showNoteModal}
						workOrderId={workOrderId}
					/>
					{showTimeCardTroubleshootModal &&
						<TimeCardTroubleshootModal
							accountId={accountId}
							allAccountEntries={entriesBasedOnTimeZone}
							closeModal={this.hideTimeCardTroubleshootModal}
							hasAvailableUnassignedEntries={availableUnassignedEntries ? availableUnassignedEntries.length : 0}
							hasUnassignedEntriesForMessage={numberOfTotalUnassignedEntries}
							isApproved={isApproved}
							onUpdateTimeSheetEntry={onUpdateTimeSheetEntry}
							openAssignUnassignedModal={this.showAssignUnassignedModal}
							overlaps={accountOverlaps}
							rejectedTSForAccount={rejectedTSForAccount}
							showEquipmentTimeError={showEquipmentTimeError}
							showModal={showTimeCardTroubleshootModal}
							timeOffset={timeOffset}
							timeZoneInUse={timeZoneInUse}
							userFullName={userFullName}
							workOrderId={workOrderId}
						/>
					}
					{showAssignUnassignedModal &&
						<AssignUnassignedEntriesModal
							accountId={accountId}
							closeModal={this.hideAssignUnassignedModal}
							hasEntryChanges={hasEntryChanges}
							timeSheetId={id}
							unassignedEntries={availableUnassignedEntries}
							userFullName={userFullName}
							workOrderId={workOrderId}
						/>
					}
					<TimelineHistoryModal
						accountId={accountId}
						closeModal={this.hideTimelineHistoryModal}
						showModal={showTimelineHistoryModal}
						timeZoneInUse={timeZoneInUse}
						timezoneSourceType={timezoneSourceType}
						workOrderId={workOrderId}
					/>
					<ConfirmationModal
						body={TimeSheetRow.NO_CHANGES_ON_REJECTED_TIME_SHEET(code, superintendentApprovalStatus.userName)}
						closeModal={this.hideNoChangesOnRejectModal}
						closeText="Cancel"
						confirmAction={this.onContinueToSignatureModal}
						confirmText="Continue"
						modalStyle="warning"
						showModal={showNoChangesOnRejectModal}
						size="sm"
						title="No changes made to Rejected Time Sheet"
					/>
					<CertificationStatusModal
						closeModal={this.hideCertificationStatusModal}
						onSubmit={this.onCertStatusSubmit}
						openCompanyPolicyModal={this.onCompanyPolicyOpen}
						showModal={showCertificationStatusModal}
					/>
					<CompanyPolicyModal
						closeModal={this.onCompanyPolicyClose}
						showModal={showCompanyPolicyModal}
					/>
					<ConfirmationModal
						body={TimeSheetRow.INVALID_TIMESHEET}
						closeModal={this.hideInvalidTimesheetModal}
						confirmAction={this.hideInvalidTimesheetModal}
						confirmText="Close"
						modalStyle="warning"
						showCancel={false}
						showModal={shouldShowInvalidTimesheetModal}
						size="sm"
						title="Time Sheet is not valid"
					/>
					<ConfirmationModal
						body={TimeSheetRow.REMOVE_FIELD_WORKER_MODAL_BODY(userFullName, code)}
						closeModal={this.hideRemoveTimeSheetModal}
						closeText="Cancel"
						confirmAction={this.onRemove}
						confirmText="Remove"
						modalStyle="danger"
						showModal={shouldShowRemoveTimeSheetModal}
						size="sm"
						title="Remove Field Worker's Timesheet from Work Order"
					/>
					<ConfirmationModal
						body={TimeSheetRow.END_SHIFT_MODAL_BODY}
						closeModal={this.hideEndShiftConfirmationModal}
						closeText="Cancel"
						confirmAction={this.confirmEndShift}
						confirmText="End Shift & Sign my Time Sheet"
						modalStyle="warning"
						showModal={showEndShiftConfirmationModal}
						title="End Shift & Sign my Time Sheet"
					/>
				</div>
			</div >
		);
	}
}

const mapStateToProps = (state: RootState) => ({
	certStatus: (getFormValues(FORMS.CERTIFICATION_STATUS_FORM)(state) as CertificationStatusForm)?.certificationStatus,
	certReason: (getFormValues(FORMS.CERTIFICATION_STATUS_FORM)(state) as CertificationStatusForm)?.reason,
});

function mapDispatchToProps() {
	return {
		removeTimeSheet: TimeSheetActions.removeTimeSheet,
		hasTimeSheetOverlaps: TimeSheetActions.hasTimeSheetOverlaps,
		endShift: TimeSheetActions.endShift,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	connector
);

export default enhance(TimeSheetRow);
